import { useEffect } from "react";
import { create } from "zustand";

export type AgentEditorTabId =
  | "persona"
  | "instructions"
  | "identity"
  | "user"
  | "policy"
  | "access";

type AgentEditorUiState = {
  dirtyTabs: Set<AgentEditorTabId>;
  hasDirtyTabs: boolean;
  setTabDirty: (tab: AgentEditorTabId, dirty: boolean) => void;
  clearAll: () => void;
};

export const useAgentEditorUiStore = create<AgentEditorUiState>((set) => ({
  dirtyTabs: new Set(),
  hasDirtyTabs: false,
  setTabDirty: (tab, dirty) =>
    set((state) => {
      const next = new Set(state.dirtyTabs);
      if (dirty) {
        next.add(tab);
      } else {
        next.delete(tab);
      }
      if (next.size === state.dirtyTabs.size && [...next].every((t) => state.dirtyTabs.has(t))) {
        return state;
      }
      return { dirtyTabs: next, hasDirtyTabs: next.size > 0 };
    }),
  clearAll: () => set({ dirtyTabs: new Set(), hasDirtyTabs: false }),
}));

/**
 * Convenience hook for tab components to register their dirty state.
 * Automatically cleans up on unmount.
 */
export function useAgentEditorDirty(tabId: AgentEditorTabId, isDirty: boolean) {
  const setTabDirty = useAgentEditorUiStore((s) => s.setTabDirty);
  useEffect(() => {
    setTabDirty(tabId, isDirty);
    return () => setTabDirty(tabId, false);
  }, [tabId, isDirty, setTabDirty]);
}
